import { useEffect, useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import ReactTable from "../../components/ReactTable";
import Pagination from "../../components/Pagination";
import { useSelector } from "react-redux";
import { FiEdit } from "react-icons/fi";
import { toast } from "react-toastify";

/* UI status values */
const STATUS = {
  APPROVE: "approve",   // maps to payload "completed"
  REJECT: "rejected",   // maps to payload "rejected"
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="text-xl">✕</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default function MyClient() {
  const [clients, setClients] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [total, setTotal] = useState(0);
  const pageSize = 5;
  const { token } = useSelector((state) => state.login);

  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;

  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bidsDraft, setBidsDraft] = useState([]);

  const [statusChoice, setStatusChoice] = useState(""); // 'approve' | 'rejected' | ''
  const [uiError, setUiError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [remarksOpen, setRemarksOpen] = useState(false);
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = async (currentPage = page, searchTerm = debouncedSearch) => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      params.append("page", String(currentPage));
      params.append("pageSize", String(pageSize));

      const res = await axios.get(`${base}/clientIpoInvestment?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setClients(res.data?.data?.list || []);
      setTotal(res.data?.data?.totalRecords || 0);
    } catch (err) {
      console.error("API error", err);
    }
  };

  useEffect(() => {
    fetchUsers(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch]);

  const handleOpenEdit = (row) => {
    setSelected(row);
    const arr = Array.isArray(row?.bidsArrayForAsbaDetails)
      ? row.bidsArrayForAsbaDetails.map((b) => ({ ...b }))
      : [];
    setBidsDraft(arr);

    const initial = (row?.asbaFormInvestmentStatus || "").toString().toLowerCase();
    const mapped =
      initial === "completed" ? STATUS.APPROVE :
      initial === "approved" ? STATUS.APPROVE :
      initial === "rejected" ? STATUS.REJECT : "";
    setStatusChoice(mapped);

    setUiError("");
    setRemarks("");
    setEditOpen(true);
  };

  const updateBidId = (idx, value) => {
    const nxt = [...bidsDraft];
    nxt[idx] = { ...nxt[idx], bidid: value };
    setBidsDraft(nxt);
  };

  const onChangeStatus = (value) => {
    setStatusChoice(value);
    setUiError("");
    if (value === STATUS.REJECT) setRemarksOpen(true);
  };

  const onSave = async () => {
    setUiError("");
    if (!statusChoice) {
      setUiError("Please select a status (Approve or Reject).");
      return;
    }

    // build updated array with bidId for payload (for both statuses)
    const updatedWithBidId = bidsDraft.map((b) => ({ ...b, bidId: b.bidid }));

    try {
      setIsSaving(true);

      if (statusChoice === STATUS.APPROVE) {
        // Approve: every bidid must be present
        const missing = updatedWithBidId
          .map((b, i) => ({ i, ok: Boolean(b?.bidid && b.bidid.trim()) }))
          .filter((x) => !x.ok)
          .map((x) => x.i + 1);
        if (missing.length) {
          setUiError(
            `Bid ID is required for all rows when approving. Missing at row${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}.`
          );
          setIsSaving(false);
          return;
        }

        await axios.put(
          `${base}/clientIpoInvestment/${selected.id}/updateBidStatus`,
          {
            status: "completed",
            bidsArrayForAsbaDetails: updatedWithBidId,
            // remarks optional on approve
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success("Status updated to COMPLETED.");
        setEditOpen(false);
        await fetchUsers();
        return;
      }

      // Reject: remarks required, include updated bids array (with bidId)
      if (!remarks.trim()) {
        setRemarksOpen(true);
        setUiError("Remarks are required for rejection.");
        setIsSaving(false);
        return;
      }

      await axios.put(
        `${base}/clientIpoInvestment/${selected.id}/updateBidStatus`,
        {
          status: "rejected",
          bidsArrayForAsbaDetails: updatedWithBidId,
          remarks,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Status updated to REJECTED.");
      setEditOpen(false);
      await fetchUsers();
    } catch (err) {
      console.error("Update status failed:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while updating status.";
      setUiError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const totals = useMemo(() => {
    const qty = bidsDraft.reduce((s, b) => s + Number(b.quantity || 0), 0);
    const amt = bidsDraft.reduce((s, b) => s + Number(b.totalOrder || 0), 0);
    return { qty, amt };
  }, [bidsDraft]);

  const columns = [
    { accessorKey: "id", header: "ID", cell: (info) => info.getValue() },
    {
      accessorKey: "user.name",
      header: "Client Name",
      cell: (info) => info.row.original.user?.name,
    },
    { accessorKey: "nameOfApplication", header: "Application", cell: (info) => info.getValue() },
    { accessorKey: "applicationNumber", header: "App No.", cell: (info) => info.getValue() },
    {
      accessorKey: "orderAmount",
      header: "Amount",
      cell: (info) => `₹ ${Number(info.getValue()).toLocaleString()}`,
    },
    {
      accessorKey: "asbaFormUrl",
      header: "ASBA Form",
      cell: (info) => {
        const handleDownload = async () => {
  const fileUrl = info.row.original.asbaFormUrl;

  // Check if the URL exists before proceeding
  if (!fileUrl) {
    return; // Prevent further execution if the URL is not available
  }

  // Fetch the file as a blob
  const response = await fetch(fileUrl);
  const blob = await response.blob(); // Get the file as a Blob object

  // Create a temporary URL for the Blob
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob); // Create an object URL for the Blob

  // Dynamically derive the file extension from the URL (if possible)
  const fileName = fileUrl.split('/').pop(); // Get the last part of the URL as the filename
  link.download = fileName; // Use the filename from the URL or provide a default one

  // Trigger the download by simulating a click
  link.click();
};

return (
  <button
    onClick={handleDownload}
    className="px-2 py-1 underline text-[#035c98] rounded text-xs"
    disabled={!info.row.original.asbaFormUrl} // Disable the button if the URL is not available
  >
    Download ASBA Form
  </button>
);


      },
    },
    {
      accessorKey: "asbaFormInvestmentStatus",
      header: "Status",
      cell: (info) => {
        const v = (info.getValue() || "").toString().toLowerCase();
        const badge =
          v === "rejected" ? "bg-red-100 text-red-800" :
          v === "completed" || v === "approved" ? "bg-green-100 text-green-800" :
          "bg-yellow-100 text-yellow-800";
        return <span className={`px-2 py-1 rounded text-xs ${badge}`}>{info.getValue()}</span>;
      },
    },
    {
      accessorKey: "remarksFromAdmin",
      header: "Remarks",
      cell: (info) => {
       
        return <span className={`px-2 py-1 rounded text-xs text-red-500`}>{info.getValue()}</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (info) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleOpenEdit(info.row.original)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit / Change Status"
          >
            <FiEdit size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">My Client List</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Search client..."
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="pl-9 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FaSearch className="absolute left-3 top-2.5 text-gray-400" />
        </div>
      </div>

      <ReactTable key={JSON.stringify(clients)} columns={columns} defaultData={clients} />

      <Pagination total={total} currentPage={page} pageSize={pageSize} onPageChange={setPage} />

      {editOpen && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={() => setEditOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-5xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <div className="text-sm text-gray-600">
                  Client: <span className="font-medium">{selected?.user?.name || "-"}</span>
                </div>
                <div className="text-sm text-gray-600">
                  App No: <span className="font-medium">{selected?.applicationNumber || "-"}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Change status</label>
                <select
                  value={statusChoice}
                  onChange={(e) => onChangeStatus(e.target.value)}
                  className="border rounded px-2 py-1"
                >
                  <option value="">Select</option>
                  <option value={STATUS.APPROVE}>Approve</option>
                  <option value={STATUS.REJECT}>Reject</option>
                </select>
              </div>
            </div>

            <div className="p-4">
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 border-b">#</th>
                      <th className="text-left p-2 border-b">Bid ID</th>
                      <th className="text-left p-2 border-b">Rate</th>
                      <th className="text-left p-2 border-b">Series</th>
                      <th className="text-left p-2 border-b">Order No</th>
                      <th className="text-left p-2 border-b">Quantity</th>
                      <th className="text-left p-2 border-b">Total Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bidsDraft.length === 0 ? (
                      <tr>
                        <td className="p-3 text-center text-gray-500" colSpan={7}>
                          No bids found for this application.
                        </td>
                      </tr>
                    ) : (
                      bidsDraft.map((b, i) => (
                        <tr key={i} className="odd:bg-white even:bg-gray-50">
                          <td className="p-2 border-b">{i + 1}</td>
                          <td className="p-2 border-b">
                            <input
                              className={`w-full border rounded p-1 ${
                                statusChoice === STATUS.APPROVE && !b.bidid?.trim()
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                              value={b.bidid || ""}
                              onChange={(e) => updateBidId(i, e.target.value)}
                              placeholder="Bid ID"
                            />
                            {statusChoice === STATUS.APPROVE && !b.bidid?.trim() && (
                              <div className="text-xs text-red-600 mt-1">Required when approving</div>
                            )}
                          </td>
                          <td className="p-2 border-b">{b.rate}</td>
                          <td className="p-2 border-b">{b.series}</td>
                          <td className="p-2 border-b">{b.orderno}</td>
                          <td className="p-2 border-b">{b.quantity}</td>
                          <td className="p-2 border-b">{b.totalOrder}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {bidsDraft.length > 0 && (
                    <tfoot>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="p-2 border-t" colSpan={5}>Totals</td>
                        <td className="p-2 border-t">{totals.qty}</td>
                        <td className="p-2 border-t">₹ {Number(totals.amt).toLocaleString()}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              {uiError && <p className="text-red-600 mt-3">{uiError}</p>}

              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setEditOpen(false)} className="px-3 py-2 rounded border" disabled={isSaving}>
                  Cancel
                </button>
                <button onClick={onSave} className="px-3 py-2 rounded bg-gray-900 text-white disabled:opacity-60" disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {remarksOpen && (
        <Modal title="Add Remarks (Reject)" onClose={() => setRemarksOpen(false)}>
          <div className="grid gap-3">
            <label className="text-sm">
              <span className="block text-gray-600 mb-1">
                Remarks <span className="text-red-600">*</span>
              </span>
              <textarea
                rows={4}
                className="w-full border rounded p-2"
                placeholder="Reason for rejection…"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRemarksOpen(false)} className="px-3 py-2 rounded border">
                Close
              </button>
              <button
                onClick={() => {
                  if (!remarks.trim()) return;
                  setRemarksOpen(false);
                }}
                className="px-3 py-2 rounded bg-gray-900 text-white"
              >
                Save Remarks
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
